import { useState, useEffect } from 'react';
import { api } from '../services/api';

/**
 * ContatosTab — CRUD de contatos de uma conta (tabela `contatos_conta`).
 *
 * Compartilhado entre ContaFormModal (aba Contatos da conta) e LeadFormModal
 * (aba Contatos do lead, que opera sobre `lead.conta_id`). Assim qualquer
 * edição feita no lead reflete automaticamente em /contas e vice-versa —
 * fonte única de verdade: `contatos_conta`.
 *
 * Props:
 *   - contaId: id da conta alvo. Se null/undefined, mostra estado vazio
 *     pedindo para vincular/salvar o lead primeiro.
 *   - emptyHint (opcional): mensagem customizada quando contaId for falsy.
 */
/**
 * leadPrincipal (opcional): { nome, cargo, telefone, email } extraído dos campos
 * legados do lead (contato_nome/funcao/telefone/email). Exibido como entrada fixada
 * no topo quando preenchido, mesmo que não exista em contatos_conta.
 */
// Mensagem de erro da API para a tela. O `request` de services/api.js joga um
// Error com `body.error` (mensagem do servidor) e `status`.
function mensagemDeErro(err, acao) {
  const detalhe = err?.body?.error || err?.message || 'erro desconhecido';
  const status = err?.status ? ` (HTTP ${err.status})` : '';
  return `Não foi possível ${acao}: ${detalhe}${status}`;
}

export default function ContatosTab({ contaId, emptyHint, leadPrincipal }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | contato
  const [form, setForm] = useState({
    nome: '', cargo: '', email: '', telefone: '', principal: false, obs: '',
  });

  const reload = () => {
    if (!contaId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    api.getContatosConta(contaId)
      .then(data => { setRows(data); setErro(null); })
      .catch(err => {
        setRows([]);
        setErro(mensagemDeErro(err, 'carregar os contatos'));
        console.error('[ContatosTab] falha ao carregar contatos:', err);
      })
      .finally(() => setLoading(false));
  };
  useEffect(reload, [contaId]);

  function startEdit(c) {
    setForm({
      nome: c.nome || '', cargo: c.cargo || '', email: c.email || '',
      telefone: c.telefone || '', principal: c.principal === 1, obs: c.obs || '',
    });
    setEditing(c);
  }

  function startNew() {
    setForm({ nome: '', cargo: '', email: '', telefone: '', principal: false, obs: '' });
    setEditing('new');
  }

  async function save() {
    if (!form.nome.trim()) return;
    const payload = { ...form, conta_id: contaId, principal: form.principal ? 1 : 0 };
    try {
      if (editing === 'new') await api.createContatoConta(payload);
      else await api.updateContatoConta(editing.id, payload);
    } catch (err) {
      // Mantém o formulário aberto com o que foi digitado — falha de rede ou
      // permissão não pode custar o trabalho do usuário.
      setErro(mensagemDeErro(err, 'salvar o contato'));
      console.error('[ContatosTab] falha ao salvar contato:', err);
      return;
    }
    setErro(null);
    setEditing(null);
    reload();
  }

  async function remove(id) {
    if (!confirm('Excluir contato?')) return;
    try {
      await api.deleteContatoConta(id);
    } catch (err) {
      setErro(mensagemDeErro(err, 'excluir o contato'));
      console.error('[ContatosTab] falha ao excluir contato:', err);
      return;
    }
    setErro(null);
    reload();
  }

  const inp = 'w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#00DBFF]';

  if (!contaId) {
    return (
      <div className="text-sm text-gray-500 py-6 text-center">
        {emptyHint || 'Vincule uma conta ao lead para gerenciar contatos.'}
      </div>
    );
  }

  const hasLeadPrincipal = leadPrincipal?.nome?.trim();

  if (loading) return <div className="text-sm text-gray-400">Carregando...</div>;

  return (
    <div className="space-y-3">
      {erro && (
        <div role="alert" className="border border-red-300 bg-red-50 text-red-700 rounded-lg px-3 py-2 text-xs">
          {erro}
        </div>
      )}

      {/* Contato principal herdado dos campos legados do lead */}
      {hasLeadPrincipal && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2.5">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold">
              principal
            </span>
            <span className="text-[10px] text-amber-600">registrado na aba Dados</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-[#002233]">{leadPrincipal.nome}</span>
            {leadPrincipal.cargo && (
              <span className="text-xs text-gray-500">— {leadPrincipal.cargo}</span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {[leadPrincipal.email, leadPrincipal.telefone].filter(Boolean).join(' • ') || '—'}
          </div>
        </div>
      )}

      {editing && (
        <div className="border border-[#00DBFF]/40 bg-[#00DBFF]/5 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Nome *" value={form.nome}
              onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} className={inp} />
            <input placeholder="Cargo" value={form.cargo}
              onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))} className={inp} />
            <input placeholder="E-mail" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inp} />
            <input placeholder="Telefone" value={form.telefone}
              onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))} className={inp} />
          </div>
          <textarea placeholder="Observação" value={form.obs}
            onChange={e => setForm(f => ({ ...f, obs: e.target.value }))} rows={2} className={inp} />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={form.principal}
                onChange={e => setForm(f => ({ ...f, principal: e.target.checked }))} />
              Contato principal
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setEditing(null)}
                className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">Cancelar</button>
              <button type="button" onClick={save}
                className="px-3 py-1 text-xs bg-[#00DBFF] text-[#002233] font-semibold rounded">Salvar</button>
            </div>
          </div>
        </div>
      )}

      {!editing && (
        <button type="button" onClick={startNew}
          className="text-xs text-[#00DBFF] hover:underline">+ Novo contato</button>
      )}

      {rows.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center">Nenhum contato cadastrado.</div>
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {rows.map(c => (
            <div key={c.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-[#002233]">{c.nome}</span>
                  {c.cargo && <span className="text-xs text-gray-500">— {c.cargo}</span>}
                  {c.principal === 1 && (
                    <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                      principal
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {[c.email, c.telefone].filter(Boolean).join(' • ') || '—'}
                </div>
                {c.obs && <div className="text-xs text-gray-400 mt-0.5 italic">{c.obs}</div>}
              </div>
              <div className="flex gap-1 ml-2">
                <button type="button" onClick={() => startEdit(c)}
                  className="text-xs text-gray-500 hover:text-[#00DBFF] px-2 py-1">Editar</button>
                <button type="button" onClick={() => remove(c.id)}
                  className="text-xs text-gray-500 hover:text-red-500 px-2 py-1">Excluir</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
